import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  LinearProgress,
  Chip,
  Avatar,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import { mockInstallers, mockSchedule } from './mockData';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

// Define which demo jobs are actually viewable
const VIEWABLE_DEMO_JOBS = {
  'J2024-087': '/jobs/pm-demo-job', // Main demo job with full functionality
};

// Job details for tooltips
const MOCK_JOB_INFO = {
  'J2024-045': 'HSE School District - Multi-year project',
  'J2024-089': 'Westfield Middle School Gym - AV Installation',
  'J2024-091': "Fisher's Quick Delivery - Microphone Add",
  'J2024-093': 'Zionsville Town Hall - AV Upgrade',
};

export default function WeeklySchedule() {
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const calculateDayHours = (installer, day) => {
    const dayJobs = mockSchedule[installer]?.[day] || [];
    return dayJobs.reduce((sum, job) => sum + job.hours, 0);
  };

  const calculateWeekHours = (installer) => {
    return days.reduce((sum, day) => sum + calculateDayHours(installer, day), 0);
  };

  const handleJobClick = (jobNumber, event) => {
    // Check if this is a viewable demo job
    if (VIEWABLE_DEMO_JOBS[jobNumber]) {
      window.location.href = VIEWABLE_DEMO_JOBS[jobNumber];
    } else {
      // For display-only demo jobs, show a message
      event.preventDefault();
      event.stopPropagation();
      setSnackbar({
        open: true,
        message: `${jobNumber} is a display-only demo job. Click J2024-087 to see the full interactive demo.`
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: '' });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Weekly Schedule - All Installers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        This view shows all installers for the week - something you can't see in Jobber's week view
      </Typography>

      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: 150 }}>Installer</TableCell>
              {days.map((day) => (
                <TableCell key={day} sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {day}
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 'bold', width: 180 }}>Week Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mockInstallers.map((installer) => {
              const weekHours = calculateWeekHours(installer.name);
              const isOverloaded = weekHours > 40;
              const isNearCapacity = weekHours >= 38 && weekHours <= 40;

              return (
                <TableRow key={installer.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: installer.color,
                          width: 24,
                          height: 24,
                          fontSize: '0.75rem',
                        }}
                      >
                        {installer.name[0]}
                      </Avatar>
                      <Typography variant="body2">{installer.name}</Typography>
                    </Box>
                  </TableCell>

                  {days.map((day) => {
                    const dayJobs = mockSchedule[installer.name]?.[day] || [];
                    const dayHours = calculateDayHours(installer.name, day);

                    return (
                      <TableCell key={day} sx={{ verticalAlign: 'top', p: 1 }}>
                        {dayJobs.length > 0 ? (
                          <Box>
                            {dayJobs.map((job, idx) => {
                              const isViewable = VIEWABLE_DEMO_JOBS.hasOwnProperty(job.job);
                              const jobInfo = MOCK_JOB_INFO[job.job] || job.client;

                              return (
                                <Tooltip
                                  key={idx}
                                  title={
                                    <Box>
                                      <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                        {job.job}
                                      </Typography>
                                      <Typography variant="caption" display="block">
                                        {jobInfo}
                                      </Typography>
                                      {!isViewable && (
                                        <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                          Display-only demo
                                        </Typography>
                                      )}
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Chip
                                    label={`${job.job} (${job.hours}h)`}
                                    size="small"
                                    variant="outlined"
                                    color={isViewable ? "success" : "default"}
                                    onClick={(e) => handleJobClick(job.job, e)}
                                    sx={{
                                      mb: 0.5,
                                      display: 'block',
                                      width: 'fit-content',
                                      mx: 'auto',
                                      fontSize: '0.7rem',
                                      cursor: 'pointer',
                                      borderColor: isViewable ? 'success.main' : 'divider',
                                      borderWidth: isViewable ? 1.5 : 1,
                                      bgcolor: isViewable ? 'success.50' : 'background.paper',
                                      '&:hover': {
                                        bgcolor: isViewable ? 'success.100' : 'action.hover',
                                        borderColor: isViewable ? 'success.dark' : 'primary.main',
                                      },
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                            <LinearProgress
                              variant="determinate"
                              value={(dayHours / 8) * 100}
                              sx={{
                                mt: 1,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: 'grey.200',
                              }}
                              color={dayHours > 8 ? 'error' : 'primary'}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                textAlign: 'center',
                                mt: 0.5,
                                color: dayHours > 8 ? 'error.main' : 'text.secondary',
                              }}
                            >
                              {dayHours}/8h
                            </Typography>
                          </Box>
                        ) : (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', textAlign: 'center' }}
                          >
                            —
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}

                  <TableCell>
                    <Box sx={{ px: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isOverloaded ? 'bold' : 'normal',
                          color: isOverloaded ? 'error.main' : isNearCapacity ? 'warning.main' : 'text.primary',
                        }}
                      >
                        {weekHours}/{installer.maxHours} hours
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(weekHours / installer.maxHours) * 100}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 3,
                        }}
                        color={isOverloaded ? 'error' : isNearCapacity ? 'warning' : 'success'}
                      />
                      {isNearCapacity && !isOverloaded && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                          Near capacity
                        </Typography>
                      )}
                      {isOverloaded && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          Overloaded!
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Capacity Summary */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Team Capacity</Typography>
          <Typography variant="h5">
            {mockInstallers.reduce((sum, i) => sum + calculateWeekHours(i.name), 0)}/
            {mockInstallers.length * 40} hrs
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">At Risk</Typography>
          <Typography variant="h5" color="warning.main">
            {mockInstallers.filter(i => {
              const hours = calculateWeekHours(i.name);
              return hours >= 38 && hours <= 40;
            }).length} installers
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Typography variant="body2" color="text.secondary">Overloaded</Typography>
          <Typography variant="h5" color="error">
            {mockInstallers.filter(i => calculateWeekHours(i.name) > 40).length} installers
          </Typography>
        </Paper>
      </Box>

      {/* Snackbar for display-only job notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}